import { create } from 'zustand';
import type { TranslationKey } from '@/i18n';

export type ToastKind = 'info' | 'success' | 'warning' | 'error';

export interface Toast {
  readonly id: number;
  readonly kind: ToastKind;
  readonly messageKey: TranslationKey;
}

export const MAX_VISIBLE_TOASTS = 3;
export const TOAST_DURATION_MS = 4000;

export interface NotificationState {
  readonly toasts: readonly Toast[];
  /** Shows a toast. A toast with the same message is replaced instead of stacked. */
  notify(messageKey: TranslationKey, kind?: ToastKind): void;
  dismiss(id: number): void;
  clear(): void;
}

let nextToastId = 1;

/** Central notification queue. Only user-visible outcomes should be reported — not every action. */
export const useNotificationStore = create<NotificationState>()((set) => ({
  toasts: [],
  notify: (messageKey, kind = 'info') =>
    set((state) => {
      const toast: Toast = { id: nextToastId++, kind, messageKey };
      const others = state.toasts.filter((existing) => existing.messageKey !== messageKey);
      return { toasts: [...others, toast].slice(-MAX_VISIBLE_TOASTS) };
    }),
  dismiss: (id) => set((state) => ({ toasts: state.toasts.filter((toast) => toast.id !== id) })),
  clear: () => set({ toasts: [] }),
}));
